import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import EconomicActivityDeleteModal from "./EconomicActivityDeleteModal";
import EconomicActivityUpdateModal from "./EconomicActivityUpdateModal";
import EconomicActivity from "../../models/economic-activity";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import EconomicActivityCrudActions from "./EconomicActivityCrudActions";
import { EconomicActivityCard } from "./EconomicActivityMobileCard";
import { useIsMobile } from "../../hooks/is-mobile";

const ITEMS_PER_PAGE = 10;

const EconomicActivityCrud = () => {
  const fields = [
    { key: "code", _classes: ["text-center"], label: "Código" },
    { key: "description", _classes: ["text-center"], label: "Descripción" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [showEconomicActivityEditModal, setShowEconomicActivityEditModal] =
    useState(false);
  const [showEconomicActivityDeleteModal, setShowEconomicActivityDeleteModal] =
    useState(false);
  const [economicActivity, setEconomicActivity] = useState<
    EconomicActivity | undefined
  >(undefined);

  const [loading, setLoading] = useState(true);
  const [economicActivities, setEconomicActivities] = useState<
    EconomicActivity[]
  >([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    return { page: page };
  };

  const fetchEconomicActivities = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());
    const economicActivitiesStatus = await getList<EconomicActivity>(
      "/economic_activities/",
      limit,
      offset,
      additionalParams,
    );

    if (economicActivitiesStatus.status === SUCCESS) {
      if (economicActivitiesStatus.data !== undefined) {
        const count = economicActivitiesStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setEconomicActivities(economicActivitiesStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = economicActivitiesStatus.detail
        ? economicActivitiesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  /*const reset = () => {
    history.push(history.location.pathname);
  };*/

  const onAddClick = () => {
    setEconomicActivity(undefined);
    setShowEconomicActivityEditModal(true);
  };

  const onUpdateClick = (economicActivity: EconomicActivity) => {
    setEconomicActivity(economicActivity);
    setShowEconomicActivityEditModal(true);
  };

  const onDeleteClick = (economicActivity: EconomicActivity) => {
    setEconomicActivity(economicActivity);
    setShowEconomicActivityDeleteModal(true);
  };

  const onEconomicActivityEditCancel = () => {
    setShowEconomicActivityEditModal(false);
  };

  const onEconomicActivityEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEconomicActivityEditModal(false);
    setEconomicActivities([]);
    setLoading(true);
    //setTotalPages(1);
    //setCurrentPage(1);
    //reset();
    fetchEconomicActivities();
  };

  const onEconomicActivityDeleteCancel = () => {
    setShowEconomicActivityDeleteModal(false);
  };

  const onEconomicActivityDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowEconomicActivityDeleteModal(false);
    setEconomicActivities([]);
    setLoading(true);
    fetchEconomicActivities();
  };

  useEffect(() => {
    fetchEconomicActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("economicActivities")) {
        return;
      }
      fetchEconomicActivities();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    setLoading(true);
    fetchEconomicActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const isMobile = useIsMobile();

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Actividades Económicas</h3>
              </div>
              {!taxPayer.isDemo && (
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </div>
              )}
            </CCardHeader>
            <CCardBody>
              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {economicActivities.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      economicActivities.map((entry) => (
                        <EconomicActivityCard
                          key={entry.id}
                          economicActivity={entry}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                          isDemo={!!taxPayer.isDemo}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={economicActivities}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      actions: (item: EconomicActivity) => {
                        return (
                          <EconomicActivityCrudActions
                            economicActivity={item}
                            onUpdateClick={onUpdateClick}
                            onDeleteClick={onDeleteClick}
                            isDemo={!!taxPayer.isDemo}
                          />
                        );
                      },
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <EconomicActivityUpdateModal
        show={showEconomicActivityEditModal}
        onCancel={onEconomicActivityEditCancel}
        onSuccess={onEconomicActivityEditSuccess}
        economicActivity={economicActivity}
      />
      <EconomicActivityDeleteModal
        show={showEconomicActivityDeleteModal}
        onCancel={onEconomicActivityDeleteCancel}
        onSuccess={onEconomicActivityDeleteSuccess}
        economicActivity={economicActivity}
      />
    </>
  );
};

export default EconomicActivityCrud;
